import { API_TAGS } from '../../constants/api';
import { API_CORE_URL, API_NLP_SERVICE_URL } from '../../constants/env';
import { apiSlice } from '../apiSlice';
import { MESSAGES } from './botsMessages';

export const botsSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    fetchBots: builder.query({
      query: ({ orgId, urlParams = {} } = { urlParams: {} }) => ({
        url: orgId ? `organizations/${orgId}/tenants` : `tenants`,
        method: 'GET',
        params: {
          ...urlParams,
          includeSettings: true,
          includePlugins: true
        },
        errorMessage: {
          showMessage: true,
          messages: MESSAGES.FETCH_BOTS.ERROR,
          errorCodesToIgnore: [401, 403]
        }
      }),
      transformResponse: (response) => {
        const { items } = response;
        const sortedBots = items?.sort((a, b) => (a.displayName > b.displayName) ? 1 : -1);
        return {
          items: sortedBots,
          total: response.total
        };
      },
      providesTags: (result, error, args) => {
        return [
          { type: API_TAGS.BOTS, id: 'LIST' }
        ];
      }
    }),

    fetchBot: builder.query({
      query: ({ orgId, tenantId, urlParams = {} }) => ({
        url: `organizations/${orgId}/tenants/${tenantId}`,
        method: 'GET',
        params: {
          ...urlParams,
          includeSettings: true,
          includeSystemSettings: true,
          includePipelines: true,
          includePlugins: true
        },
        errorMessage: {
          showMessage: true,
          messages: MESSAGES.FETCH_BOT.ERROR
        }
      }),
      providesTags: (result, error, args) => {
        return [{ type: API_TAGS.BOTS, id: args.tenantId }];
      }
    }),

    // The most recently used bot,
    // Or the most popular bot,
    // Or the first one in the bots list
    fetchFirstAvailableBot: builder.query({
      queryFn: async (args, { getState, dispatch }) => {
        try {
          const { orgId } = args;

          // 1. If we have recent bots, return the most recent one
          const state = getState();
          const recentBots = state.bots.recentBots || [];

          if (recentBots.length > 0) {
            return { data: recentBots[0] };
          }

          // 2. If no recent bots, fetch dashboard data to find the most popular bot
          const { data: dashboardData } = await dispatch(
            apiSlice.endpoints.fetchDashboard.initiate({ orgId })
          );

          const mostPopularBot = dashboardData?.mostUsedTenants?.[0];

          if (mostPopularBot) {
            return { data: mostPopularBot };
          }

          // 3. Fetch all bots
          const { data: botsData, error: botsError } = await dispatch(
            botsSlice.endpoints.fetchBots.initiate({ orgId, urlParams: { pageSize: 100 } })
          );

          if (botsError) {
            return { error: botsError };
          }

          const bots = botsData?.items || [];

          if (bots.length === 0) {
            return { error: { status: 404, data: 'No bots found' } };
          }

          return { data: bots[0] };
        } catch (error) {
          return { error: { status: 500, data: error.message } };
        }
      },
      providesTags: () => {
        return [{ type: API_TAGS.BOTS, id: 'FIRST_AVAILABLE' }];
      }
    }),

    fetchBotDefaultPrompt: builder.query({
      query: ({ orgId, category, urlParams = {} }) => ({
        url: `organizations/${orgId}/categories/${category}/prompt`,
        method: 'GET',
        API_URL: API_NLP_SERVICE_URL,
        params: {},
        errorMessage: {
          showMessage: false,
          messages: MESSAGES.FETCH_BOT_DEFAULT_PROMPT.ERROR
        }
      })
    }),

    fetchBotPublic: builder.query({
      query: ({ tenantId, urlParams = {} }) => ({
        url: `public/tenants/${tenantId}`,
        method: 'GET',
        params: {
          ...urlParams,
          includeSettings: true
        },
        withAuth: false,
        errorMessage: {
          showMessage: true,
          messages: MESSAGES.FETCH_BOT_PUBLIC.ERROR
        }
      }),
      providesTags: (result, error, args) => {
        return [{ type: API_TAGS.BOTS, id: args.tenantId }];
      }
    }),

    createBot: builder.mutation({
      query: ({ orgId, data, urlParams, successMessage = { showMessage: true } }) => ({
        url: `organizations/${orgId}/tenants`,
        method: 'POST',
        body: data,
        params: {
          ...urlParams
        },
        includeResponseHeaders: true,
        errorMessage: {
          showMessage: true,
          messages: MESSAGES.CREATE_BOT.ERROR
        },
        successMessage: {
          ...successMessage,
          messages: MESSAGES.CREATE_BOT.SUCCESS
        },
        responseType: 'text'
      }),
      transformResponse: (response) => {
        return response.headers['hbf-created-resource-id'];
      },
      invalidatesTags: (result, error, args) => {
        if (error) {
          return [];
        }
        return [
          { type: API_TAGS.BOTS, id: 'LIST' },
          { type: API_TAGS.BOTS, id: args.tenantId }
        ];
      }
    }),

    updateBot: builder.mutation({
      query: ({ orgId, tenantId, data, urlParams = {}, successMessage = { showMessage: true }, resourcesToInvalidate = [] }) => ({
        url: `organizations/${orgId}/tenants/${tenantId}`,
        method: 'PATCH',
        params: {
          ...urlParams
        },
        body: data,
        errorMessage: {
          showMessage: true,
          messages: MESSAGES.UPDATE_BOT.ERROR
        },
        successMessage: {
          ...successMessage,
          messages: MESSAGES.UPDATE_BOT.SUCCESS
        }
      }),
      transformResponse: (response) => true,
      transformErrorResponse: (error) => false,
      invalidatesTags: (result, error, args) => {
        if (error) {
          return [];
        }

        const tags = [
          { type: API_TAGS.BOTS, id: args.tenantId },
          { type: API_TAGS.BOTS, id: 'LIST' }
        ];

        const extraTags = (args.resourcesToInvalidate || []).map(resource => {
          if (typeof resource === 'string') {
            return { type: resource, id: 'LIST' };
          }
          return {
            type: resource.tag || resource.type,
            id: resource.id || 'LIST'
          };
        });

        return [...tags, ...extraTags].filter(t => t.type);
      }
    }),

    deleteBot: builder.mutation({
      query: ({ orgId, tenantId, urlParams = {}, successMessage = { showMessage: true } }) => ({
        url: `organizations/${orgId}/tenants/${tenantId}`,
        method: 'DELETE',
        params: {
          ...urlParams
        },
        errorMessage: {
          showMessage: true,
          messages: MESSAGES.DELETE_BOT.ERROR
        },
        successMessage: {
          ...successMessage,
          messages: MESSAGES.DELETE_BOT.SUCCESS
        }
      }),
      transformResponse: (response) => true,
      transformErrorResponse: (error) => false,
      invalidatesTags: (result, error, args) => {
        if (error) {
          return [];
        }
        return [
          { type: API_TAGS.BOTS, id: 'LIST' },
          { type: API_TAGS.BOTS, id: 'FIRST_AVAILABLE' }
        ];

      }
    }),

    cloneBot: builder.mutation({
      query: ({ orgId, tenantId, targetOrgId, data, urlParams = {}, successMessage = { showMessage: true } }) => ({
        url: `organizations/${orgId}/tenants/clone`,
        method: 'POST',
        params: {
          ...urlParams,
          sourceTenantId: tenantId,
          targetOrganizationId: targetOrgId
        },
        body: data,
        errorMessage: {
          showMessage: true,
          messages: MESSAGES.CLONE_BOT.ERROR
        },
        includeResponseHeaders: true,
        successMessage: {
          ...successMessage,
          messages: MESSAGES.CLONE_BOT.SUCCESS
        },
        responseType: 'text'
      }),
      transformResponse: (response) => {
        return response.responseContents;
      },
      invalidatesTags: (result, error, args) => {
        if (error) {
          return [];
        }
        return [{ type: API_TAGS.BOTS, id: 'LIST' }];
      }
    }),

    trainBot: builder.mutation({
      query: ({ orgId, tenantId, urlParams = {}, successMessage = { showMessage: true }, apiUrl = API_CORE_URL }) => ({
        url: `organizations/${orgId}/tenants/${tenantId}/train`,
        API_URL: apiUrl,
        method: 'POST',
        params: {
          ...urlParams
        },
        errorMessage: {
          showMessage: true,
          messages: MESSAGES.TRAIN_BOT.ERROR
        },
        successMessage: {
          ...successMessage,
          messages: MESSAGES.TRAIN_BOT.SUCCESS
        }
      }),
      transformResponse: (response) => true,
      transformErrorResponse: (response) => false,
      invalidatesTags: (result, error, args) => {
        if (error) {
          return [];
        }
        return [{ type: API_TAGS.BOTS, id: args.tenantId }];
      }
    }),

    clearCache: builder.mutation({
      query: ({ orgId, tenantId, urlParams = {}, successMessage = { showMessage: true } }) => ({
        url: `organizations/${orgId}/tenants/${tenantId}/cache`,
        method: 'DELETE',
        errorMessage: {
          showMessage: true,
          messages: MESSAGES.CLEAR_CACHE.ERROR
        },
        successMessage: {
          showMessage: true,
          messages: MESSAGES.CLEAR_CACHE.SUCCESS
        }
      }),
      transformResponse: (response) => true,
      invalidatesTags: (result, error, args) => {
        if (error) {
          return [];
        }
        return [{ type: API_TAGS.BOTS, id: args.tenantId }];
      }
    }),

    processMessage: builder.query({
      query: ({ orgId, tenantId, data, urlParams = {}, apiUrl = API_CORE_URL }) => ({
        url: `organizations/${orgId}/tenants/${tenantId}/process`,
        API_URL: apiUrl,
        method: apiUrl === API_CORE_URL ? 'GET' : 'POST',
        ...data && { body: data },
        params: {
          ...urlParams
        },
        errorMessage: {
          showMessage: true,
          messages: MESSAGES.PROCESS_MESSAGE.ERROR
        }
      }),
      providesTags: (result, error, args) => {
        return [
          { type: API_TAGS.BOTS, id: 'PROCESS' }
        ];
      }
    }),

    processMessageWithPipelineId: builder.query({
      query: ({ orgId, tenantId, pipelineId, data, urlParams = {}, apiUrl = API_CORE_URL }) => ({
        url: `organizations/${orgId}/tenants/${tenantId}/nlp-pipelines/${pipelineId}/process`,
        API_URL: apiUrl,
        method: apiUrl === API_CORE_URL ? 'GET' : 'POST',
        ...data && { body: data },
        params: {
          ...urlParams
        },
        errorMessage: {
          showMessage: true,
          messages: MESSAGES.PROCESS_MESSAGE.ERROR
        }
      }),
      providesTags: (result, error, args) => {
        return [
          { type: API_TAGS.BOTS, id: 'PROCESS' }
        ];
      }
    })
  })
});

export const {
  useFetchBotsQuery,
  useLazyFetchBotsQuery,
  useFetchBotQuery,
  useFetchFirstAvailableBotQuery,
  useFetchBotDefaultPromptQuery,
  useLazyFetchBotQuery,
  useFetchBotPublicQuery,
  useLazyFetchBotPublicQuery,
  useCreateBotMutation,
  useUpdateBotMutation,
  useDeleteBotMutation,
  useCloneBotMutation,
  useTrainBotMutation,
  useClearCacheMutation,
  useLazyProcessMessageQuery,
  useLazyProcessMessageWithPipelineIdQuery
} = botsSlice;
